import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api, { getSocket, initSocket } from '../api';
import { useAuth } from './AuthContext';
import { useCurrentWeek } from './CurrentWeekContext';
import { useSeason } from './SeasonContext';
import { useLeagueSettings } from './LeagueSettingsContext';
import { getCurrentWeek } from '../utils/dateUtils';
import { useNavigate, useLocation } from 'react-router-dom';
import { isPublicRoute } from '../constants/publicRoutes';
import LockNotification from '../components/LockNotification';
import ConfirmationDialog from '../components/ConfirmationDialog';
import { useToast } from './ToastContext';
import { formatLineVerificationMessage, getPickTypeDisplay, formatLine, resolveLockLineForSubmit, formatLockConfirmDetail } from '../utils/lineDataUtils';
import {
  buildLockSuccessToast,
  formatLockFailureMessage,
  getUserNewLocks,
  isNewLock,
} from '../utils/lockToastUtils';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';

const GameContext = createContext();

// Utility function at module level
const isPickMismatched = (pick, mismatchedLines) => {
  return mismatchedLines.some(mismatch =>
    mismatch.gameId === pick.gameId &&
    mismatch.pickType === pick.pickType
  );
};

const updateLockInCache = (lock, gameId, userId, won_lost, margin) => {
  if (lock.gameId === gameId && lock.userId === userId) {
    return {
      ...lock,
      won_lost,
      margin,
      status: 'completed'
    };
  }
  return lock;
};

// Utility function at module level
const updateCacheForWeek = (weekLocks, gameId, userId, won_lost, margin) => {
  return weekLocks.map(lock => updateLockInCache(lock, gameId, userId, won_lost, margin));
};

const updateEntireCache = (cache, data) => {
  const newCache = { ...cache };
  Object.keys(newCache).forEach(weekNumber => {
    newCache[weekNumber] = updateCacheForWeek(
      newCache[weekNumber],
      data.gameId,
      data.userId,
      data.won_lost,
      data.margin
    );
  });
  return newCache;
};

// Utility functions at module level
const isWatchMatchingLock = (watch, lock) =>
  lock.gameId === watch.gameId && lock.pickType === watch.pickType;

const filterUnlockedWatches = (watches, currentUserLocks) => {
  return watches.filter(watch =>
    !currentUserLocks.some(lock => isWatchMatchingLock(watch, lock))
  );
};

// Utility function at module level
const filterLocksFromCache = (cache, lockId) => {
  const newCache = { ...cache };
  Object.keys(newCache).forEach(weekNumber => {
    newCache[weekNumber] = newCache[weekNumber].filter(lock => lock.id !== lockId);
  });
  return newCache;
};

// At module level
const updateGameWithLiveData = (game, update) => {
  if (!update || !game) return game;
  return {
    ...game,
    game_status: update.game_status || game.game_status,
    period: update.period || game.period,
    clock: update.clock || game.clock,
    situation: update.situation || game.situation,
    possession: update.possession || game.possession,
    last_play: update.last_play || game.last_play,
    home_score: update.home_score ?? game.home_score,
    away_score: update.away_score ?? game.away_score,
    home_line_scores: update.home_line_scores || game.home_line_scores,
    away_line_scores: update.away_line_scores || game.away_line_scores,
    betting: {
      ...game.betting,
      spread: update.betting?.spread ?? game.betting?.spread,
      over_under: update.betting?.over_under ?? game.betting?.over_under
    }
  };
};

// Utility function for debounced updates
const debouncedGameUpdates = debounce((updates, setGames, setGameUpdates) => {
  if (!updates) return;

  setGames(prevGames => {
    const gameUpdatesChanges = {};
    const updatedGames = prevGames.map(game => {
      const update = updates.find(u => u.id === game.id);
      if (!update || !game) return game;

      gameUpdatesChanges[game.id] = {
        scores: update.home_score !== game.home_score || update.away_score !== game.away_score,
        clock: update.clock !== game.clock || update.period !== game.period,
        timestamp: Date.now()
      };

      return updateGameWithLiveData(game, update);
    });

    setGameUpdates(prev => ({ ...prev, ...gameUpdatesChanges }));
    return updatedGames;
  });
}, 1000); // 1 second debounce

export const GameProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useAuth();
  const [redirected, setRedirected] = useState(false);
  const { currentWeek, calendar, setCurrentWeek, isLoading: isCurrentWeekLoading } = useCurrentWeek();
  const { effectiveSeason, loading: seasonLoading } = useSeason();
  const { settings: leagueSettings } = useLeagueSettings();
  const [games, setGames] = useState([]);
  const [loadedGamesWeek, setLoadedGamesWeek] = useState(null);
  const [isGamesLoading, setIsGamesLoading] = useState(false);
  const [userPicks, setUserPicks] = useState([]);
  const [leaguePicks] = useState([]);
  const [watchedPicks, setWatchedPicks] = useState([]);
  const [watchLoading, setWatchLoading] = useState({});
  const [rankingsInfo, setRankingsInfo] = useState(null);
  const [error, setError] = useState(null);
  const [isPeeking, setIsPeeking] = useState(false);
  const [locksCache, setLocksCache] = useState({});
  const [adminLocks, setAdminLocks] = useState([]);
  const [notification, setNotification] = useState(null);
  const [activeUserCount, setActiveUserCount] = useState(0);
  const [verificationState, setVerificationState] = useState({
    isOpen: false,
    mismatchedLines: [],
    originalRequest: null
  });
  const [lockConfirmState, setLockConfirmState] = useState({
    isOpen: false,
    pick: null,
    weekNumber: null,
  });
  const [lineChanges] = useState({});
  const [animatingGames, setAnimatingGames] = useState({});
  const [userId, setUserId] = useState(null);
  const [byeWeekState, setByeWeekState] = useState({
    showOption: false,
    isPending: false,
    weekNumber: null,
    bye_used: false
  });
  const [gameUpdates, setGameUpdates] = useState({});
  const [userSeasonLocks, setUserSeasonLocks] = useState([]);
  const { showToast } = useToast();
  const watchedPicksRef = useRef(watchedPicks);
  const lockSuccessCallbackRef = useRef(null);

  const clearLockSuccessCallback = useCallback(() => {
    lockSuccessCallbackRef.current = null;
  }, []);

  useEffect(() => {
    watchedPicksRef.current = watchedPicks;
  }, [watchedPicks]);

  useEffect(() => {
    if (user?.id) {
      setUserId(user.id);

      api.getByeStatus()
        .then(response => {
          const byeUsed = response.data.bye_used;
          // Respect user's default_bye_visibility preference, but hide if bye is already used
          const shouldShowOption = !byeUsed && user?.default_bye_visibility;
          setByeWeekState(prev => ({
            ...prev,
            bye_used: byeUsed,     // Store the bye_used status
            showOption: shouldShowOption,  // Show option based on user preference and bye status
            isPending: false
          }));
        })
        .catch(error => {
          console.error('Error setting bye week state:', error);
          setByeWeekState(prev => ({
            ...prev,
            bye_used: false,
            showOption: false,
            isPending: false
          }));
        });
    }
  }, [user]);

  const handleGameUpdate = useCallback((updatedGame) => {
    setGames(prevGames => prevGames.map(game =>
      game.id === updatedGame.id ? { ...game, ...updatedGame } : game
    ));
  }, []);

  const handlePickUpdate = useCallback((updatedPick) => {
    setUserPicks(prevPicks => {
      const existingPickIndex = prevPicks.findIndex(pick => pick.id === updatedPick.id);
      if (existingPickIndex !== -1) {
        return prevPicks.map(pick =>
          pick.id === updatedPick.id ? { ...pick, ...updatedPick } : pick
        );
      } else {
        return [...prevPicks, updatedPick];
      }
    });
  }, []);

  const handleToggleWatchPickResponse = useCallback((response) => {
    if (response.status === 'success') {
      setWatchedPicks(prevWatched => {
        const pickId = `${response.gameId}_${response.pickType}`;

        if (response.isWatched) {
          const existingPickIndex = prevWatched.findIndex(pick => pick.id === pickId);
          if (existingPickIndex !== -1) {
            return prevWatched.map((pick, index) =>
              index === existingPickIndex
                ? { ...pick, watchLine: response.watchLine, id: pickId }
                : pick
            );
          } else {
            const newPick = {
              id: pickId,
              gameId: response.gameId,
              pickType: response.pickType,
              watchLine: response.watchLine,
              liveLine: response.liveLine,
              weekNumber: response.weekNumber,
              status: 'watched',
              userId: userId
            };
            return [...prevWatched, newPick];
          }
        } else {
          return prevWatched.filter(pick => pick.id !== pickId);
        }
      });
    }
    setWatchLoading(prev => ({ ...prev, [`${response.gameId}_${response.pickType}`]: false }));
  }, [userId]);

  const handleLiveGameUpdates = useCallback((updates) => {
    debouncedGameUpdates(updates, setGames, setGameUpdates);
  }, []);

  const fetchLiveGameData = useCallback(() => {
    const socket = getSocket();
    if (socket) {
      socket.emit('live_game_updates', (updates) => {
        handleLiveGameUpdates(updates);
      });
    }
  }, [handleLiveGameUpdates]);

  const handlePendingStatusUpdate = useCallback((updatedPick) => {
    setWatchedPicks(prev => prev.map(pick =>
      pick.id === updatedPick.id ? { ...pick, status: updatedPick.status } : pick
    ));
  }, []);

  const handleAdminLocksFetched = useCallback((data) => {
    setAdminLocks(data.locks);
  }, []);


  const handlePurgeClientLock = useCallback((data) => {
    setLocksCache(prev => filterLocksFromCache(prev, data.lockId));
  }, []);

  const handlePurgeAllClientLocks = useCallback(() => {
    setLocksCache({});
  }, []);

  const fetchLocksForWeek = useCallback(async (week) => {
    const socket = getSocket();
    if (socket && effectiveSeason) {
      socket.emit('request_locks', { week, season: effectiveSeason });
    }
  }, [effectiveSeason]);

  const handleLocksPurged = useCallback(() => {
    fetchLocksForWeek(currentWeek);
  }, [currentWeek, fetchLocksForWeek]);

  const currentWeekData = useMemo(() => {
    return calendar.find(weekData => weekData.week === currentWeek);
  }, [calendar, currentWeek]);

  const maxPicks = useMemo(() => {
    return currentWeekData?.max_picks || 1;
  }, [currentWeekData]);

  const handleLocksData = useCallback((data) => {
    if (data?.week && data?.locks) {
      setLocksCache(prevCache => ({
        ...prevCache,
        [data.week]: data.locks
      }));
    } else {
      console.error('[handleLocksData] Received invalid locks data:', data);
    }
  }, []);

  const clearByeWeekState = useCallback(() => {
    setByeWeekState({
      showOption: false,
      isPending: false,
      weekNumber: null,
      bye_used: false
    });
    localStorage.removeItem('byeWeekState');
  }, []);


  // Handle watched picks cleanup after successful lock
  const handleWatchedPicksCleanup = useCallback((allConfirmedLocks) => {
    if (!user || !allConfirmedLocks.length) return;

    const weekNumber = allConfirmedLocks[0].weekNumber;
    const weekData = calendar.find((w) => w.week === weekNumber);
    const maxPicksForLockedWeek = weekData?.max_picks || 1;
    const currentUserLocks = allConfirmedLocks.filter(lock => lock.userId === user.id);
    const userLocksForWeek = currentUserLocks.filter(lock => lock.weekNumber === weekNumber);

    setWatchedPicks(prev => {
      let updatedWatches = filterUnlockedWatches(prev, currentUserLocks);

      if (maxPicksForLockedWeek === 1 || userLocksForWeek.length >= maxPicksForLockedWeek) {
        updatedWatches = updatedWatches.filter(watch => watch.weekNumber !== weekNumber);
      }

      return updatedWatches;
    });

    const hasByeLock = currentUserLocks.some(lock => lock.pickType === 'bye');
    if (hasByeLock) {
      clearByeWeekState();
    }
  }, [user, calendar, clearByeWeekState]);

  const handleVerifyLockLines = useCallback((data) => {
    const mismatchDetails = data.mismatchedLines.map(line => ({
      gameId: line.gameId,
      pickType: line.pickType,
      oldLine: line.lockLine,
      newLine: line.currentLine,
      home_team: line.home_team,
      away_team: line.away_team,
      home_team_abv: line.home_team_abv,
      away_team_abv: line.away_team_abv,
      displayMessage: formatLineVerificationMessage(
        {
          id: line.gameId,
          home_team: line.home_team,
          away_team: line.away_team,
          home_team_abv: line.home_team_abv,
          away_team_abv: line.away_team_abv
        },
        line.pickType,
        line.currentLine,
        line.lockLine
      )
    }));

    setVerificationState({
      isOpen: true,
      mismatchedLines: mismatchDetails,
      originalRequest: data.originalRequest
    });
  }, []);

    useEffect(() => {
    if (!user && !redirected && !isPublicRoute(location.pathname)) {
      setRedirected(true);
      navigate('/login', { replace: true });
    }
  }, [user, navigate, redirected, location.pathname]);


  const handleGameAnimation = useCallback(async (gameId, phase, isCurrentUser = false) => {
    // Don't animate if not current user's lock
    if (!isCurrentUser) {
      console.log('Animation skipped: not current user');
      return;
    }

    // Check if game already has locks
    const hasExistingLocks = Object.values(locksCache).some(weekLocks =>
      weekLocks.some(lock => lock.gameId === gameId)
    );

    // Don't animate if game already has locks
    if (hasExistingLocks) {
      console.log('Animation skipped: game already has locks');
      return;
    }

    if (phase === 'fadeOut') {
      setAnimatingGames(prev => ({ ...prev, [gameId]: 'fadeOut' }));
      await new Promise(resolve => setTimeout(resolve, 500));
    } else if (phase === 'fadeIn') {
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Find the specific game card
      const gameCard = document.querySelector(`[data-game-id="${gameId}"]`);
      if (gameCard) {
        // Calculate position to scroll to (slightly above the game card)
        const cardPosition = gameCard.getBoundingClientRect().top;
        const offset = 200; // Increased from 100 to 200
        const scrollPosition = cardPosition + window.pageYOffset - offset;

        // Smooth scroll to position
        window.scrollTo({
          top: scrollPosition,
          behavior: 'smooth'
        });

        await new Promise(resolve => setTimeout(resolve, 500));
      }

      await new Promise(resolve => setTimeout(resolve, 750));
      setAnimatingGames(prev => ({ ...prev, [gameId]: 'fadeIn' }));
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  }, [locksCache]);

  const revertLockingPicksWithToast = useCallback((error) => {
    if (!watchedPicksRef.current.some((pick) => pick.status === 'locking')) {
      return;
    }

    console.error('Failed to lock picks:', error);
    showToast({
      message: formatLockFailureMessage(error),
      severity: 'error',
    });
    setWatchedPicks((prev) =>
      prev.map((pick) =>
        pick.status === 'locking' ? { ...pick, status: 'watched' } : pick
      )
    );
    clearLockSuccessCallback();
  }, [showToast, clearLockSuccessCallback]);

  const handlePicksLockedError = revertLockingPicksWithToast;

  const handlePicksLockedSuccess = useCallback(async (data) => {
    if (!data.confirmedLocks?.length) return;

    const weekNumber = data.confirmedLocks[0].weekNumber;
    const existingLocks = locksCache[weekNumber] || [];
    const newLocks = data.confirmedLocks.filter((newLock) =>
      isNewLock(newLock, existingLocks)
    );
    const userNewLocks = getUserNewLocks(data.confirmedLocks, existingLocks, user?.id);
    const toastPayload = buildLockSuccessToast(userNewLocks, weekNumber);

    if (userNewLocks.length > 0 && lockSuccessCallbackRef.current) {
      const onSuccess = lockSuccessCallbackRef.current;
      lockSuccessCallbackRef.current = null;
      onSuccess();
    }

    if (toastPayload) {
      showToast(toastPayload);
    }

    for (const lock of newLocks) {
      await handleGameAnimation(lock.gameId, 'fadeOut', lock.userId === user?.id);
    }

    handleLocksData({
      week: weekNumber,
      locks: data.confirmedLocks,
    });
    handleWatchedPicksCleanup(data.confirmedLocks);

    for (const lock of newLocks) {
      await handleGameAnimation(lock.gameId, 'fadeIn', lock.userId === user?.id);
    }
  }, [
    locksCache,
    user?.id,
    showToast,
    handleGameAnimation,
    handleLocksData,
    handleWatchedPicksCleanup,
  ]);

  // Add new handler for pick completion
  const handlePickCompleted = useCallback((data) => {
    setLocksCache(prevCache => updateEntireCache(prevCache, data));
  }, []);

    useEffect(() => {
    if (user) {
      initSocket();
      const socket = getSocket();

      if (socket) {
        socket.on('error', (error) => {
          if (error.message?.includes('authentication') || error.message?.includes('unauthorized')) {
            navigate('/login', { replace: true });
          } else if (error.message?.includes('bye week')) {
            // Clear the bye week state
            setByeWeekState(prev => ({
              ...prev,
              showOption: false,
              isPending: false,
              bye_used: true
            }));
            // Show notification
            setNotification(`Error: ${error.message}`);
          } else {
            revertLockingPicksWithToast(error);
          }
          console.error('[socket:error]', error);
        });

        // Connection events
        socket.on('connect', () => {
        });

        socket.on('disconnect', () => {
        });

        // Game and Pick updates
        socket.on('game_update', handleGameUpdate);
        socket.on('pick_update', handlePickUpdate);
        socket.on('live_game_updates', handleLiveGameUpdates);

        // Watch-related events
        socket.on('toggle_watch_pick_response', handleToggleWatchPickResponse);
        socket.on('pending_status_update', handlePendingStatusUpdate);
        socket.on('all_watched_picks_update', (watchedPicksData) => {
          const watchedPicksWithIds = watchedPicksData.map(pick => ({
            ...pick,
            id: `${pick.gameId}_${pick.pickType}`
          }));
          setWatchedPicks(watchedPicksWithIds);
        });

        // Lock-related events
        socket.on('locks_data', handleLocksData);
        socket.on('picks_locked_success', handlePicksLockedSuccess);
        socket.on('picks_locked_error', handlePicksLockedError);
        socket.on('pick_completed', handlePickCompleted);

        // Admin-related events
        socket.on('admin_locks_fetched', handleAdminLocksFetched);
        socket.on('locks_updated', handleLocksPurged);
        socket.on('purge_client_lock', handlePurgeClientLock);
        socket.on('purge_all_client_locks', handlePurgeAllClientLocks);

        socket.on('lock_notification', async (data) => {
          console.log('Calling animation with data:', {
            gameId: data.gameId,
            userId: data.userId,
            currentUser: user?.id
          });

          // Display notification and update UI
          const pickTypeDisplay = getPickTypeDisplay(data.pickType, data.homeTeamAbbr, data.awayTeamAbbr);

          const gameDisplay = `${data.awayTeamAbbr}@${data.homeTeamAbbr}`;

          let message;
          if (data.pickType === 'over' || data.pickType === 'under') {
            message = `${data.username} has locked in ${pickTypeDisplay}${Math.abs(data.lockLine)} for ${gameDisplay} Week ${data.weekNumber}`;
          } else {
            message = `${data.username} has locked in ${pickTypeDisplay} ${formatLine(data.lockLine)} for ${gameDisplay} Week ${data.weekNumber}`;
          }

          setNotification(message);
        });

        socket.on('verify_lock_lines', handleVerifyLockLines);

        return () => {
          // Connection cleanup
          socket.off('connect');
          socket.off('disconnect');
          socket.off('error');

          // Game and Pick cleanup
          socket.off('game_update', handleGameUpdate);
          socket.off('pick_update', handlePickUpdate);
          socket.off('live_game_updates', handleLiveGameUpdates);

          // Watch-related cleanup
          socket.off('toggle_watch_pick_response', handleToggleWatchPickResponse);
          socket.off('pending_status_update', handlePendingStatusUpdate);
          socket.off('all_watched_picks_update');

          // Lock-related cleanup
          socket.off('locks_data', handleLocksData);
          socket.off('picks_locked_success', handlePicksLockedSuccess);
          socket.off('picks_locked_error', handlePicksLockedError);
          socket.off('pick_completed', handlePickCompleted);


          // Admin cleanup
          socket.off('admin_locks_fetched', handleAdminLocksFetched);
          socket.off('locks_updated', handleLocksPurged);
          socket.off('purge_client_lock', handlePurgeClientLock);
          socket.off('purge_all_client_locks', handlePurgeAllClientLocks);

          socket.off('lock_notification');
          socket.off('verify_lock_lines', handleVerifyLockLines);
        };
      }
    }
  }, [
    user,
    handleGameUpdate,
    handlePickUpdate,
    handleToggleWatchPickResponse,
    handleLiveGameUpdates,
    handlePendingStatusUpdate,
    handleAdminLocksFetched,
    handleLocksPurged,
    handlePurgeClientLock,
    handlePurgeAllClientLocks,
    handlePicksLockedError,
    handlePicksLockedSuccess,
    revertLockingPicksWithToast,
    handleLocksData,
    handleWatchedPicksCleanup,
    setWatchedPicks,
    navigate,
    handleVerifyLockLines,
    handlePickCompleted,
    handleGameAnimation,
  ]);

  const toggleWatchPick = useCallback((gameId, pickType, weekNumber, watchLine, liveLine, isBetSlipOpen) => {
    const socket = getSocket();
    if (socket) {
      const pickId = `${gameId}_${pickType}`;
      socket.emit('toggle_watch_pick', { gameId, pickType, weekNumber, watchLine, liveLine });
      setWatchLoading(prev => ({ ...prev, [pickId]: true }));

      setWatchedPicks(prev => {
        const existingPickIndex = prev.findIndex(pick => pick.id === pickId);

        if (existingPickIndex !== -1) {
          // If the pick exists, remove it (unwatch)
          return prev.filter(pick => pick.id !== pickId);
        } else {
          // If the pick doesn't exist, add it (watch)
          return [...prev, { id: pickId, gameId, pickType, watchLine, liveLine, weekNumber, userId: user?.id }];
        }
      });
      // Only trigger peek effect if BetSlip is not already open
      if (!isBetSlipOpen) {
        setIsPeeking(true);
        setTimeout(() => setIsPeeking(false), 3000);
      }
    }
  }, [user?.id]);

  const fetchGames = useCallback(async (week) => {
    if (week === null || week === undefined) {
      console.error('Attempted to fetch games with null or undefined week');
      return;
    }
    if (effectiveSeason === null || effectiveSeason === undefined) {
      console.error('Attempted to fetch games with null or undefined season');
      return;
    }
    setIsGamesLoading(true);
    try {
      const response = await api.fetchGames(week, effectiveSeason);

      // Handle new API response structure with rankings info
      if (response.data.games) {
        setGames(response.data.games);
        // Store rankings info for potential use in UI
        if (response.data.rankingsInfo) {
          console.log('Rankings info received:', response.data.rankingsInfo);
          setRankingsInfo(response.data.rankingsInfo);
        } else {
          setRankingsInfo(null);
        }
      } else {
        // Fallback for old API response structure
        setGames(response.data);
        setRankingsInfo(null);
      }
      setLoadedGamesWeek(week);
    } catch (error) {
      if (error.response?.status === 404) {
        setGames([]);
        setRankingsInfo(null);
        setLoadedGamesWeek(week);
      } else {
        console.error('Error fetching games:', error);
        setError('Failed to fetch games');
      }
    } finally {
      setIsGamesLoading(false);
    }
  }, [effectiveSeason]);

  const fetchWatchedPicks = useCallback(() => {
    if (!user?.id) return;

    const socket = getSocket();
    if (socket) {
      socket.emit('fetch_all_watched_picks');
    }
  }, [user?.id]);

  useEffect(() => {
    if (!isCurrentWeekLoading && !seasonLoading && currentWeek !== null && effectiveSeason !== null && user?.id) {
      fetchGames(currentWeek);
      fetchWatchedPicks();
    }
  }, [currentWeek, isCurrentWeekLoading, seasonLoading, effectiveSeason, user?.id, fetchGames, fetchWatchedPicks]);

  const togglePendingStatus = useCallback((pickId) => {
    const socket = getSocket();
    if (socket) {
      socket.emit('toggle_pending_status', { pickId });
      // Optimistically update the watchedPicks
      setWatchedPicks(prev => prev.map(pick => {
        if (pick.id === pickId) {
          const newStatus = pick.status === 'pending' ? 'watched' : 'pending';
          return { ...pick, status: newStatus };
        }
        return pick;
      }));
    }
  }, []);

  const getLocksForWeek = useCallback((weekNumber) => {
    return locksCache[weekNumber] || [];
  }, [locksCache]);

  const changeWeek = useCallback(async (newWeek) => {
    setCurrentWeek(newWeek);
    if (!locksCache[newWeek]) {
      await fetchLocksForWeek(newWeek);
    }
  }, [fetchLocksForWeek, locksCache, setCurrentWeek]);

  // Check if future week locks are allowed for a specific week
  const isFutureWeekLockAllowed = useCallback(async (weekNumber) => {
    try {
      // If it's the current week or a past week, always allow
      if (weekNumber <= currentWeek) {
        return true;
      }

      // Check if it's a bowl week (weeks 17+)
      const calendarWeek = calendar.find(week => week.week === weekNumber);
      if (calendarWeek && calendarWeek.season_type === 'postseason' && weekNumber >= 17) {
        return true; // Bowl weeks always allow all picks
      }

      // For future weeks, check the league settings from context
      return leagueSettings?.allow_future_week_locks || false;
    } catch (error) {
      console.error('Error checking future week lock allowance:', error);
      return false; // Default to not allowing if there's an error
    }
  }, [currentWeek, calendar, leagueSettings]);

  // Optional: Override backend current week with frontend calculation for UI consistency
  // This can be useful if the backend and frontend calculations differ
  useEffect(() => {
    if (calendar.length > 0 && !isCurrentWeekLoading) {
      const calculatedCurrentWeek = getCurrentWeek(calendar);
      const backendCurrentWeek = currentWeek;

      // Only override if there's a significant difference (more than 1 week)
      if (Math.abs(calculatedCurrentWeek - backendCurrentWeek) > 1) {
        console.log('GameContext: Frontend/backend current week mismatch detected:', {
          frontend: calculatedCurrentWeek,
          backend: backendCurrentWeek,
          'setting to': calculatedCurrentWeek
        });
        setCurrentWeek(calculatedCurrentWeek);
      }
    }
  }, [calendar, setCurrentWeek, isCurrentWeekLoading, currentWeek]);

  const lockPicks = useCallback((picksToLock, weekNumber) => {
    const socket = getSocket();
    if (socket && user) {
      const locksToSend = picksToLock.map(pick => {
        // If it's a bye pick, return simplified lock data
        if (pick.pickType === 'bye') {
          return {
            userId: user.id,
            gameId: null,
            pickType: 'bye',
            weekNumber: weekNumber,
            lockLine: 0,
            teamId: null,
            pickLeadTime: null
          };
        }

        // Otherwise, process regular pick data
        const game = games.find(g => g.id === pick.gameId);
        const pickLeadTime = Math.floor((new Date(game.start_date) - new Date()) / 1000);
        return {
          userId: user.id,
          gameId: pick.gameId,
          pickType: pick.pickType,
          weekNumber: weekNumber,
          lockLine: pick.lockLine,
          teamId: pick.pickType === 'home' ? game.home_team.id : game.away_team.id,
          pickLeadTime: pickLeadTime
        };
      });

      // Emit the lock request to the server
      socket.emit('lock_picks', locksToSend);
    }
  }, [games, user]);

  const clearWatchesForWeek = useCallback((weekNumber) => {
    const socket = getSocket();
    if (socket) {
      socket.emit('clear_watches_for_week', { weekNumber });
      setWatchedPicks((prev) => prev.filter((pick) => pick.weekNumber !== weekNumber));
    }
  }, []);

  const buildLockConfirmDetails = useCallback((pick, weekNumber) => {
    if (pick.pickType === 'bye') {
      return [`Week ${weekNumber}: Bye Week`];
    }

    const game = games.find((g) => g.id === pick.gameId);
    return [formatLockConfirmDetail(pick, weekNumber, game)];
  }, [games]);

  const requestDirectLock = useCallback((pick, weekNumber, options = {}) => {
    lockSuccessCallbackRef.current = options.onSuccess || null;
    setLockConfirmState({
      isOpen: true,
      pick,
      weekNumber,
    });
  }, []);

  const cancelDirectLock = useCallback(() => {
    clearLockSuccessCallback();
    setLockConfirmState({
      isOpen: false,
      pick: null,
      weekNumber: null,
    });
  }, [clearLockSuccessCallback]);

  const confirmDirectLock = useCallback(() => {
    const { pick, weekNumber } = lockConfirmState;
    if (!pick || weekNumber == null) return;

    const lockLine = resolveLockLineForSubmit(pick);

    if (pick.id) {
      setWatchedPicks((prev) =>
        prev.map((p) => (p.id === pick.id ? { ...p, status: 'locking' } : p))
      );
    }

    lockPicks([{ ...pick, lockLine }], weekNumber);
    setLockConfirmState({
      isOpen: false,
      pick: null,
      weekNumber: null,
    });
  }, [lockConfirmState, lockPicks]);

  const fetchAdminLocks = useCallback((source) => {
    const socket = getSocket();
    if (socket) {
      socket.emit('admin_fetch_locks', { source });
    }
  }, []);

  const purgeAdminLock = useCallback((lockId, source) => {
    const socket = getSocket();
    if (socket) {
      socket.emit('admin_purge_lock', { lockId, source });
    }
  }, []);

  const purgeAllAdminLocks = useCallback((source) => {
    const socket = getSocket();
    if (socket) {
      socket.emit('admin_purge_all_locks', { source });
    }
  }, []);

  const fetchActiveUserCount = useCallback(async () => {
    try {
        const response = await api.getActiveUserCount();
        setActiveUserCount(response.data.count);
    } catch (error) {
        console.error('Error fetching active user count:', error);
        setActiveUserCount(0);
    }
  }, []);

  useEffect(() => {
    if (user && currentWeek) {
      fetchActiveUserCount();
    }
  }, [fetchActiveUserCount, currentWeek, user]);

  const handleVerificationResponse = useCallback((confirmed) => {
    const socket = getSocket();
    if (!socket) return;

    if (!confirmed) {
      setWatchedPicks(prev => prev.map(pick =>
        isPickMismatched(pick, verificationState.mismatchedLines)
          ? { ...pick, status: 'watched' }
          : pick
      ));
      clearLockSuccessCallback();
    }

    socket.emit('confirm_lock_lines', {
      confirmed,
      originalRequest: verificationState.originalRequest,
      mismatchedLines: verificationState.mismatchedLines
    });

    setVerificationState(prev => ({ ...prev, isOpen: false }));
  }, [verificationState, clearLockSuccessCallback]);

  const handlePickLocked = useCallback(async (lockData) => {
    try {
      if (lockData.pickType === 'bye') {
        // Clear bye week state immediately before other updates
        setByeWeekState({
          showOption: false,
          isPending: false,
          weekNumber: null,
          bye_used: true
        });

        // Update user's bye_used status
        setUser(prev => ({
          ...prev,
          bye_used: true
        }));

        // Update locks cache
        setLocksCache(prev => {
          const weekKey = `week_${lockData.weekNumber}`;
          const existingWeekLocks = prev[weekKey] || [];
          return {
            ...prev,
            [weekKey]: [...existingWeekLocks, { ...lockData, userId: user.id }]
          };
        });
      }
    } catch (error) {
      console.error('Error handling pick lock:', error);
    }
  }, [user?.id, setUser]);

  const toggleByeWeekOption = useCallback((show) => {
    setByeWeekState(prev => {
      const newState = {
        ...prev,
        showOption: show,
        // Clear pending status when hiding the option
        isPending: show ? prev.isPending : false,
        weekNumber: show ? prev.weekNumber : null
      };
      localStorage.setItem('byeWeekState', JSON.stringify(newState));
      return newState;
    });
  }, []);

  const toggleByeWeekPending = useCallback((weekNumber) => {
    setByeWeekState(prev => {
      const newState = {
        ...prev,
        isPending: !prev.isPending,
        weekNumber: !prev.isPending ? weekNumber : null
      };
      localStorage.setItem('byeWeekState', JSON.stringify(newState));
      return newState;
    });
  }, []);

  // Add this new function to get max picks for any week
  const getMaxPicksForWeek = useCallback((weekNumber) => {
    const weekData = calendar.find(weekData => weekData.week === weekNumber);
    return weekData?.max_picks || 1;
  }, [calendar]);

  const fetchUserSeasonLocks = useCallback(async () => {
    try {
      const response = await api.fetchUserSeasonLocks();
      setUserSeasonLocks(response.data.seasonLocks);
    } catch (error) {
      console.error('Error fetching season locks:', error);
    }
  }, []);

  // Add effect to fetch season locks when user changes
  useEffect(() => {
    if (user) {
      fetchUserSeasonLocks();
    }
  }, [user, fetchUserSeasonLocks]);

  const isLoading = useMemo(
    () =>
      isCurrentWeekLoading ||
      seasonLoading ||
      isGamesLoading ||
      (currentWeek != null &&
        effectiveSeason != null &&
        user?.id &&
        loadedGamesWeek !== currentWeek),
    [
      isCurrentWeekLoading,
      seasonLoading,
      isGamesLoading,
      currentWeek,
      effectiveSeason,
      user?.id,
      loadedGamesWeek,
    ]
  );

  const contextValue = useMemo(() => ({
    games,
    userPicks,
    leaguePicks,
    watchedPicks,
    setWatchedPicks,
    isLoading,
    error,
    currentWeek,
    watchLoading,
    gameUpdates,
    rankingsInfo,
    loadedGamesWeek,
    isGamesLoading,
    fetchGames,
    fetchWatchedPicks,
    toggleWatchPick,
    handleGameUpdate,
    handlePickUpdate,
    fetchLiveGameData,
    handleLiveGameUpdates,
    togglePendingStatus,
    isPeeking,
    lockPicks,
    clearWatchesForWeek,
    requestDirectLock,
    confirmDirectLock,
    cancelDirectLock,
    lockConfirmState,
    locksCache,
    setLocksCache,
    maxPicks,
    getLocksForWeek,
    changeWeek,
    calendar,
    isCurrentWeekLoading,
    adminLocks,
    fetchAdminLocks,
    purgeAdminLock,
    purgeAllAdminLocks,
    fetchLocksForWeek,
    user,
    activeUserCount,
    fetchActiveUserCount,
    verificationState,
    handleVerificationResponse,
    lineChanges,
    handlePickCompleted,
    animatingGames,
    handleGameAnimation,
    handlePickLocked,
    byeWeekState,
    toggleByeWeekOption,
    toggleByeWeekPending,
    getMaxPicksForWeek,
    clearByeWeekState,
    userSeasonLocks,
    fetchUserSeasonLocks,
    isFutureWeekLockAllowed
  }), [
    rankingsInfo,
    loadedGamesWeek,
    isGamesLoading,
    games,
    userPicks,
    leaguePicks,
    watchedPicks,
    setWatchedPicks,
    isLoading,
    error,
    currentWeek,
    watchLoading,
    gameUpdates,
    fetchGames,
    fetchWatchedPicks,
    toggleWatchPick,
    handleGameUpdate,
    handlePickUpdate,
    fetchLiveGameData,
    handleLiveGameUpdates,
    togglePendingStatus,
    isPeeking,
    lockPicks,
    clearWatchesForWeek,
    requestDirectLock,
    confirmDirectLock,
    cancelDirectLock,
    lockConfirmState,
    locksCache,
    setLocksCache,
    maxPicks,
    getLocksForWeek,
    changeWeek,
    calendar,
    isCurrentWeekLoading,
    adminLocks,
    fetchAdminLocks,
    purgeAdminLock,
    purgeAllAdminLocks,
    fetchLocksForWeek,
    user,
    activeUserCount,
    fetchActiveUserCount,
    verificationState,
    handleVerificationResponse,
    lineChanges,
    handlePickCompleted,
    animatingGames,
    handleGameAnimation,
    handlePickLocked,
    byeWeekState,
    toggleByeWeekOption,
    toggleByeWeekPending,
    getMaxPicksForWeek,
    clearByeWeekState,
    userSeasonLocks,
    fetchUserSeasonLocks,
    isFutureWeekLockAllowed
  ]);

  return (
    <GameContext.Provider value={contextValue}>
      {children}
      {notification && (
        <LockNotification
          message={notification}
          onClose={() => setNotification(null)}
        />
      )}
      {lockConfirmState.isOpen && lockConfirmState.pick && (
        <ConfirmationDialog
          title="Confirm Lock"
          message="You are about to lock in this pick. This action cannot be undone."
          details={buildLockConfirmDetails(lockConfirmState.pick, lockConfirmState.weekNumber)}
          confirmLabel="Confirm Lock"
          cancelLabel="Cancel"
          onConfirm={confirmDirectLock}
          onCancel={cancelDirectLock}
        />
      )}
    </GameContext.Provider>
  );
};

GameProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useGameContext = () => {
  const context = useContext(GameContext);
  if (context === undefined) {
    throw new Error('useGameContext must be used within a GameProvider');
  }
  return context;
};

export default GameContext;
